import React from 'react';
import clsx from 'clsx';
import { Button, Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';

const VERIFYING_STEPS = [
	'Information submitted securely',
	'Running identity and eligibility checks...',
	'Finalizing verification status',
];

const RING_RADIUS = 36;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

interface VerifyingPhaseBodyProps {
	stepIndex: number;
	percent: number;
}

export const VerifyingPhaseBody: React.FC<VerifyingPhaseBodyProps> = ({
	stepIndex,
	percent,
}) => {
	const ringOffset = RING_CIRCUMFERENCE - (percent / 100) * RING_CIRCUMFERENCE;

	return (
		<div className="min-h-[360px] flex flex-col items-center justify-center py-8">
			<div className="relative w-20 h-20 mb-6">
				<svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
					<circle
						cx="40"
						cy="40"
						r={RING_RADIUS}
						fill="none"
						strokeWidth="4"
						className="stroke-purple-150/20"
					/>
					<circle
						cx="40"
						cy="40"
						r={RING_RADIUS}
						fill="none"
						strokeWidth="4"
						strokeLinecap="round"
						strokeDasharray={RING_CIRCUMFERENCE}
						strokeDashoffset={ringOffset}
						className="stroke-purple-150 transition-[stroke-dashoffset] duration-100 ease-linear"
					/>
				</svg>
				<div className="absolute inset-0 flex items-center justify-center">
					<Icon src={Icons.verifyingIdentity} width={33} height={36} />
				</div>
			</div>
			<Typography type="semibold-24" className="text-center">
				Verifying Information
			</Typography>
			<Typography type="regular-16" className="text-center text-gray-35 mt-2">
				Please wait while we securely process and verify your information. This
				usually takes just a few moments.
			</Typography>
			<div className="flex flex-col items-center gap-3 mt-8">
				{VERIFYING_STEPS.map((step, index) => (
					<div key={step} className="flex items-center gap-3">
						{index < stepIndex ? (
							<Icon
								src={Icons.tickCircle}
								className="!text-main-green"
								width={16}
								height={16}
							/>
						) : index === stepIndex ? (
							<span className="w-5 h-5 rounded-full border-2 border-transparent border-t-purple-150 animate-spin shrink-0" />
						) : (
							<span className="w-5 h-5 rounded-full border-2 border-gray-20 shrink-0" />
						)}
						<Typography
							type="regular-14"
							className={clsx(
								index <= stepIndex ? 'text-black' : 'text-gray-20'
							)}
						>
							{step}
						</Typography>
					</div>
				))}
			</div>
		</div>
	);
};

export const VerifyingPhaseFooter: React.FC = () => (
	<div className="flex justify-end">
		<Button decoration="primary" size="fit" label="Processing..." disabled />
	</div>
);
